import React, { useEffect, useRef } from 'react';

import { renderToStaticMarkup } from 'react-dom/server';

import { Link as RouterLink } from "react-router-dom";

import {
    css, Center, Flex, Grid, GridItem, HStack,
    Icon, IconButton, Link, Text, useColorModeValue, Tooltip
} from "@chakra-ui/react";

import { SwitchColorModeButton } from "../parts/ToggleColorModeButton";

import {
    GoDeviceMobile, GoGlobe, GoLocation, GoMail
} from "react-icons/go";

import { useReactToPrint } from "react-to-print";

import { FaFolder, FaLinkedin, FaSave } from "react-icons/fa";

import projectF from "../fonts/ProjectF";
import { Helmet } from 'react-helmet';

import { frameText, hpatipeText } from '../parts/context';

const printingStripeStyle = css({
    "@media print": {
        printColorAdjust: "exact",
        WebkitPrintColorAdjust: "exact",
        background: "linear-gradient(to bottom, rgba(255, 255, 255, 0) 60%, #02f0ff 60%, #02f0ff 75%, rgba(255, 255, 255, 0) 75%)"
    }
});

const CV = () => {

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        pageStyle: "@page { size: letter }"
    });

    useEffect(() => {
        document.title = "Xun Zhang's Résumé";
    }, []);

    return (
        <Center>
            <Helmet>
                <meta name="description" content={"My résumé."} />
            </Helmet>
            <ComponentToPrint ref={componentRef} />
            <Flex direction="row" columnGap="12px" position="fixed" top="12px" right="12px">
                {navigator.userAgent.toLowerCase().includes("chrome") && <Tooltip label="Save or print" placement="left" hasArrow defaultIsOpen>
                    <IconButton
                        size="md"
                        fontSize="lg"
                        aria-label="Print or save my résumé"
                        color="current"
                        icon={<FaSave />}
                        onClick={handlePrint}
                    />
                </Tooltip>}
                <Tooltip label="Switch to portfolio" placement="bottom" hasArrow>
                    <Link as={RouterLink} to="/portfolio">
                        <IconButton
                            size="md"
                            fontSize="lg"
                            color="current"
                            icon={<FaFolder />}>
                        </IconButton></Link>
                </Tooltip>
                <SwitchColorModeButton />
            </Flex>
        </Center>
    )
};

const ContactEntry = ({ icon, link, text, isExternal, ...props }) => {
    return <HStack {...props}>
        <Icon as={icon} />
        <Link href={link} isExternal={false}>{text}</Link>
    </HStack>;
}

const MainSection = (props) => {
    return <Flex direction="column" rowGap={3}>
        <Flex direction={["column", "column", "column", "row"]} gap={[0, 0, 0, 6]} alignItems={["flex-start", "flex-start", "flex-start", "baseline"]} css={css({ "@media print": { flexDirection: "row", gap: "18", alignItems: "baseline" } })}>
            <Text fontStyle="italic" fontSize="24px" fontWeight="bold" background={props.bgColor} css={printingStripeStyle}>{props.section}</Text>
            {props.subtitle}
        </Flex>
        <Grid templateColumns={props.templateColumns} rowGap={3} columnGap={6} alignItems="left" css={props.forceGridStle}>
            {props.children}
        </Grid>
    </Flex>;
}

const MainEntry = (props) => {
    return <>
        <GridItem textColor="#798391">{props.label}</GridItem>
        <GridItem>
            {props.children}
        </GridItem>
    </>;
}


const ComponentToPrint = React.forwardRef((props, ref) => {

    const introColor = useColorModeValue("#475260", "#798391");
    const bgColor = useColorModeValue(
        "linear-gradient(to bottom, rgba(255, 255, 255, 0) 60%, #02f0ff 60%, #02f0ff 75%, rgba(255, 255, 255, 0) 75%)",
        "linear-gradient(to bottom, rgba(255, 255, 255, 0) 60%, #01a0ee 60%, #01a0ee 75%, rgba(255, 255, 255, 0) 75%)"
    );

    const borderColorLight = "#dddddd";
    const borderColorDark = "#444444";
    const tombColorLight = "#f7f7f7";
    const tombColorDark = "#1f242f";

    const w = 1048;
    const h = 1344;
    const viewBox = `0 0 ${w} ${h}`;
    const padding = 10;
    const d = `M ${padding} ${padding} L ${w - padding} ${padding} L ${w - padding} ${h - padding} L ${padding} ${h - padding} Z`;

    const nLinesMax = 142;
    const lineSpacing = 11;
    const nCharsPerLine = 183;

    let nLines = Math.floor(hpatipeText.length / nCharsPerLine) + 1;
    if (nLines > nLinesMax) {
        nLines = nLinesMax;
    }
    let lines = [];
    let linesText = [];
    let linesColorLight = [];
    let linesColorDark = [];
    for (let i = 0; i < nLines; i++) {
        lines.push(`M ${padding} ${padding + lineSpacing * (i + 1)} L ${w - padding} ${padding + lineSpacing * (i + 1)} Z`);
        linesText.push(hpatipeText.slice(i * nCharsPerLine, (i + 1) * nCharsPerLine));
        let t = Math.floor((1 - (i / nLines)) * 255);
        linesColorLight.push(tombColorLight + t.toString(16).padStart(2, "0"));
        linesColorDark.push(tombColorDark + t.toString(16).padStart(2, "0"));
    }
    const encodeSvg = (borderColor, textColor) => {
        return `/**/url("data:image/svg+xml,${encodeURIComponent(renderToStaticMarkup(
            <svg viewBox={viewBox} version="1.1" baseProfile="full" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                <defs>
                    <style type="text/css">{`
                            @font-face {
                                font-family: "ProjectF";
                                src: ${projectF} format('opentype');
                            }`
                    }
                    </style>
                </defs>
                <path id="secret" d={d} fill='transparent' />
                {lines.map((l, i) =>
                    <path id={`line${i.toString()}`} d={l} fill='transparent' key={`id-line${i.toString()}`} />)
                }
                <text width={w} fontSize={9.35} fontWeight={888} fill={borderColor} fontFamily="ProjectF">
                    <textPath xlinkHref='#secret'>{frameText}</textPath>
                </text>
                {lines.map((l, i) =>
                    <text width={w} fontSize={10} fontWeight={765} fill={textColor[i]} fontFamily="ProjectF" key={`id-text${i.toString()}`}>
                        <textPath xlinkHref={`#line${i.toString()}`}>{linesText[i]}</textPath>
                    </text>)
                }
            </svg>
        ))}")`;
    };

    const svgBorderLight = encodeSvg(borderColorLight, linesColorLight);
    const svgBorderDark = encodeSvg(borderColorDark, linesColorDark);

    const finalSvgUrl = useColorModeValue(svgBorderLight, svgBorderDark);

    const printingGridStyle = css({
        "@media print": {
            gridTemplateColumns: "150px 800px"
        }
    });

    const mapLink = "https://www.google.com/maps/place/Los+Angeles,+CA";

    return (
        <Flex pt="36px" pb="48px" px="12px" my="48px" backgroundImage={[null, null, null, finalSvgUrl]} backgroundRepeat="no-repeat" backgroundSize="100%">
            <Flex w={["100vw", "100vw", "100vw", "1000px"]} mx="12px" p="12px" direction="column" gap="20px" ref={ref}>

                <Grid templateColumns={["100%", "100%", "100%", "720px 150px"]} rowGap={1} columnGap={6} alignItems="left" css={css({ "@media print": { gridTemplateColumns: "650px 250px", gridColumnGap: "96px" } })}>
                    <Flex direction="column">
                        <Flex><Text fontWeight="bold" fontStyle="italic" fontSize="32px" background={bgColor} css={printingStripeStyle}>Xun Zhang, Ph.D.</Text></Flex>
                        <Text textColor={introColor}><i>Graduated from Rutgers University, I'm now a full-time software engineer. My research interest includes computer graphics, crowd simulation, discrete optimization, and artificial intelligence.</i></Text>
                    </Flex>
                    <Grid templateColumns={["1fr 1fr", "1fr 1fr", "1fr 1fr 1fr 1fr", "1fr"]} css={css({ "@media print": { gridTemplateColumns: "1fr" } })}>
                        <ContactEntry isExternal={true} icon={GoLocation} display={["none", "none", "none", "flex"]} css={css({ "@media print": { display: "flex" } })} link={mapLink} text="Los Angeles, CA" />
                        <ContactEntry isExternal={true} icon={GoDeviceMobile} link="tel:+18482521084" text="+1 (848) 252-1084" />
                        <ContactEntry isExternal={true} icon={GoMail} link="mailto:job@shiky.me" text="job@shiky.me" />
                        <ContactEntry isExternal={false} icon={GoGlobe} link="/portfolio" text="shiky.me/portfolio" />
                        <ContactEntry isExternal={true} icon={FaLinkedin} link="https://linkedin.com/in/xz348" text="linkedin.com/in/xz348" />
                    </Grid>
                </Grid>

                <MainSection bgColor={bgColor} section="Education" templateColumns={["100%", "100%", "100%", "140px 800px"]} forceGridStle={printingGridStyle}>
                    <MainEntry label="09/2016 - 10/2023">
                        <p><i>Ph.D. in Computer Science @ <Link href="https://www.rutgers.edu/" isExternal>Rutgers, the State University of New Jersey</Link></i></p>
                        <ul style={{ marginLeft: "18px" }}><li>Research topics included computer graphics, crowd behavior simulation and synthesis, computational narrative, and computer-aided design of architectures.</li></ul>
                    </MainEntry>
                    <MainEntry label="09/2014 - 05/2015">
                        <p><i>M.Sc. in Computer Science @ <Link href="https://www.cuhk.edu.hk/" isExternal>The Chinese University of Hong Kong</Link></i></p>
                        <ul style={{ marginLeft: "18px" }}><li>Projects included augmented reality mobile applications.</li></ul>
                        <ul style={{ marginLeft: "18px" }}><li>Awarded the <b>Dean's List</b> for outstanding academic performance.</li></ul>
                    </MainEntry>
                    <MainEntry label="09/2010 - 05/2014">
                        <p><i>B.Sc. in Electronics and Information Engineering @ <Link href="https://en.ustb.edu.cn" isExternal>University of Science and Technology Beijing</Link></i></p>
                        <ul style={{ marginLeft: "18px" }}><li>Implemented a quality control system for high-precision CNC nozzles using OpenCV and C++.</li><li>Awarded a <b>first-class</b> scholarship.</li></ul>
                    </MainEntry>
                </MainSection>

                <MainSection bgColor={bgColor} section="Work Experience" templateColumns={["100%", "100%", "100%", "140px 800px"]} forceGridStle={printingGridStyle}
                    subtitle={<Text textColor="#798391"><i>
                        More projects are available in my <Link href="/portfolio"><u>interactive portfolio</u></Link>.
                        Publications are available on <Link href="https://scholar.google.com/citations?user=VA2I3UQAAAAJ&hl=en" isExternal><u>Google Scholar</u></Link>.
                    </i></Text>}>

                    <MainEntry label="06/2024 - 11/2025">
                        <p><i>Software Engineer @ <Link href="https://www.coinbase.com/" isExternal>Coinbase, Inc.</Link></i></p>
                        <ul style={{ marginLeft: "18px" }}>
                            <li>Worked as a full-stack software engineer, maintaing internal tools for crypto liquidity management.</li>
                        </ul>
                    </MainEntry>

                    <MainEntry label="07/2019 - 09/2019">
                        <p><i>Software Engineer Internship @ <Link href="https://fcl.ethz.ch/" isExternal>ETH Zürich - Future Cities Lab</Link></i></p>
                        <ul style={{ marginLeft: "18px" }}>
                            <li>Primarily responsible for urban design and simulation software development, providing professional crowd simulation insights for the department of architecture and urban design.</li>
                        </ul>
                        <p><i>Project - Urban Design and Planning Simulation Engine</i></p>
                        <ul style={{ marginLeft: "18px" }}>
                            <li>A Unity-based computer-aided design (CAD) software, providing urban planning and crowd simulation functionalities for urban designers.</li>
                            <li>Tech owner and tech lead of the project, fully responsible for the end-to-end process.</li>
                            <li>Designed, implemented and optimized compelling simulations of virtual crowds, leveraging real-world crowd statistics data.</li>
                            <li>Collaborated with architects and engineers on interdisciplinary subjects including computer science, mathematics, and architecture.</li>
                        </ul>
                    </MainEntry>
                    <MainEntry label="06/2015 - 12/2015">
                        <p><i>Software Engineer @ <Link href="https://www.skytree.com.hk/" isExternal>Skytree Digital, Ltd.</Link></i></p>
                        <ul style={{ marginLeft: "18px" }}>
                            <li>Worked as a mobile game engineer (iOS and Android)</li>
                            <li>Played a major role in the development and maintenance of over 10 mobile games, including the popular rhythm game <Link href="https://play.google.com/store/apps/details?id=hk.com.skytree.hachihachi"><u><i>Hachi Hachi</i></u></Link> and a variety of educational mobile apps.</li>
                        </ul>
                    </MainEntry>
                    <MainEntry label="03/2016 - Present">
                        <p><i>Designer and Software Engineer @ <Link href="https://orogenstudios.com" isExternal>Orogen Studios</Link></i></p>
                        <ul style={{ marginLeft: "18px" }}>
                            <li>Working as the developer on a variety of projects across various fields, including embedded systems, video games, and more.</li>
                        </ul>
                        <p><i>Project - <Link href="https://github.com/ShikyC/Taiko-Drum-Controller-Arduino" isExternal><u>Taiko Game Controller</u></Link></i></p>
                        <ul style={{ marginLeft: "18px" }}>
                            <li>An open-source game controller hardware based on ESP32-S3 for the game <Link href="https://taiko.namco-ch.net/taiko/" isExternal>Taiko no Tatsujin</Link>.</li>
                            <li>Provided an industry-level solution for professional players and widely acclaimed by the taiko DIY community.</li>
                        </ul>
                    </MainEntry>
                </MainSection>

                <MainSection bgColor={bgColor} section="Skills" templateColumns={["100%", "100%", "100%", "150px 800px"]} forceGridStle={printingGridStyle}>
                    <MainEntry label="Coding Languages"><b>C#</b>, C, C++, <b>Python</b>, Java, Go, <b>JavaScript</b>, TypeScript</MainEntry>
                    <MainEntry label="Tools & Frameworks"><b>Unity</b>, <b>ReactJS</b>, PostgreSQL, MongoDB, Node.js, Docker, Git, DevOps, Blender, embedded systems</MainEntry>
                </MainSection>

            </Flex>
        </Flex>
    );
});

export default CV;